import { z } from "zod";
import { medicalQuestionCategorySchema } from "./medical-question";

// TES-986 (Ö-266) — the medical review case.
//
// Emelie 2026-09-28, F-04 "SLUTLIGT ÖVERORDNAT MVP-BESLUT":
// "När patienten sparar Ja eller Osäker på nya/förändrade besvär skapar eller uppdaterar systemet omedelbart ett enda separat medicinskt granskningsärende för aktuell F-04-submission."
//
// One case per F-04 round (a draft round, or a submission that came without a
// draft), supplemented as more answers are saved and on the final submit.
// The coach's "Skicka till läkare" from a chat message or a shared reflection
// creates the same model, with the source and the original text kept.

// TES-1120 — `support_thread`: staff escalated a patient's message (or her
// whole thread) from Ella Support to the doctor (TES-1110).
export const MEDICAL_REVIEW_CASE_SOURCES = [
  "weekly_checkin",
  "coach_chat",
  "shared_reflection",
  "support_thread",
] as const;
export const medicalReviewCaseSourceSchema = z.enum(
  MEDICAL_REVIEW_CASE_SOURCES,
);
export type MedicalReviewCaseSource = z.infer<
  typeof medicalReviewCaseSourceSchema
>;

// TES-1120 — the sources that are a patient's non-acute medical question
// escalated to the care team: they carry the Ö-341 SLA (two working days
// from the escalation) and are answered with a medical notice. A weekly
// check-in case is neither.
export const MEDICAL_QUESTION_CASE_SOURCES = [
  "coach_chat",
  "shared_reflection",
  "support_thread",
] as const satisfies readonly MedicalReviewCaseSource[];
export function isMedicalQuestionSource(
  source: MedicalReviewCaseSource,
): boolean {
  return (MEDICAL_QUESTION_CASE_SOURCES as readonly string[]).includes(source);
}

// `red` is "Brådskande": an acute signal. It sorts first and stays unhandled
// until a doctor has opened the originals and pressed "granskad".
export const MEDICAL_REVIEW_CASE_PRIORITIES = [
  "red",
  "high",
  "normal",
] as const;
export const medicalReviewCasePrioritySchema = z.enum(
  MEDICAL_REVIEW_CASE_PRIORITIES,
);
export type MedicalReviewCasePriority = z.infer<
  typeof medicalReviewCasePrioritySchema
>;

export const MEDICAL_REVIEW_CASE_PRIORITY_RANK: Record<
  MedicalReviewCasePriority,
  number
> = { red: 0, high: 1, normal: 2 };

export const MEDICAL_REVIEW_CASE_STATUSES = ["open", "reviewed"] as const;
export const medicalReviewCaseStatusSchema = z.enum(
  MEDICAL_REVIEW_CASE_STATUSES,
);
export type MedicalReviewCaseStatus = z.infer<
  typeof medicalReviewCaseStatusSchema
>;

// SLA per priority, in minutes, for WEEKLY CHECK-IN cases. DELIBERATELY UNSET.
// (A medical question has its own SLA since TES-1120: Ö-341's two working
// days, MEDICAL_QUESTION_SLA_WORKING_DAYS.)
//
// Emelie: "Den exakta SLA:n per prioritet kommer från det läkargodkända medicinska protokollet. Anta inga värden."
// No doctor-approved protocol with response times exists in Drive or in the
// code (the F-04 document names SLA as a field, never a number), so every
// priority is `null` and a case carries no deadline. Filling a value in here
// is a clinical decision, not a code change — see TES-986's open question.
export const MEDICAL_REVIEW_SLA_MINUTES: Record<
  MedicalReviewCasePriority,
  number | null
> = { red: null, high: null, normal: null };

export function medicalReviewSlaDueAt(
  priority: MedicalReviewCasePriority,
  from: Date,
  sla: Record<
    MedicalReviewCasePriority,
    number | null
  > = MEDICAL_REVIEW_SLA_MINUTES,
): Date | null {
  const minutes = sla[priority];
  return minutes == null ? null : new Date(from.getTime() + minutes * 60_000);
}

// The only thing a coach ever sees of a check-in whose medical answers were
// routed to the doctor — in EVERY coach view: the work-queue card, the
// check-in row, Aktuellt/Patientbild and the AI draft panel (TES-1213).
// A-01, "Medicinsk information i coachytan", verbatim:
// "Vid automatisk medicinsk routing ser coachen endast: ”Medicinsk uppföljning pågår. Ett medicinskt svar från incheckningen hanteras av ansvarig läkare. Du behöver inte bedöma eller kommentera detta.”"
// and "Samma gräns ska gälla konsekvent i alla vyer." It replaced
// "Medicinskt svar hanteras av läkare." (this constant) and "Hanteras av
// läkare" (the admin's check-in row label).
export const MEDICAL_REVIEW_NEUTRAL_STATUS =
  "Medicinsk uppföljning pågår. Ett medicinskt svar från incheckningen hanteras av ansvarig läkare. Du behöver inte bedöma eller kommentera detta.";

// One rule that fired, with when it first fired. Rules accumulate: an answer
// changed back in a later draft save never removes what was already seen.
export const medicalReviewCaseRuleSchema = z.object({
  rule: z.string(),
  firstSeenAt: z.string(),
});
export type MedicalReviewCaseRule = z.infer<typeof medicalReviewCaseRuleSchema>;

// The queue / list shape — no original answers. Those are only served by the
// detail read, which is what records that the doctor opened them.
export const medicalReviewCaseSummarySchema = z.object({
  id: z.string(),
  patientId: z.string(),
  source: medicalReviewCaseSourceSchema,
  priority: medicalReviewCasePrioritySchema,
  status: medicalReviewCaseStatusSchema,
  rules: z.array(medicalReviewCaseRuleSchema),
  ruleVersion: z.string().nullable(),
  protocolVersion: z.string().nullable(),
  formDraftId: z.string().nullable(),
  submissionId: z.string().nullable(),
  handoffId: z.string().nullable(),
  responsibleDoctorId: z.string().nullable(),
  slaDueAt: z.string().nullable(),
  firstTriggeredAt: z.string(),
  lastSourceUpdateAt: z.string(),
  submittedAt: z.string().nullable(),
  openedAt: z.string().nullable(),
  openedByDoctorId: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  reviewedByDoctorId: z.string().nullable(),
  createdAt: z.string(),
  // TES-1120 — the question's category, when the coach or Support gave one.
  category: medicalQuestionCategorySchema.nullable(),
  // TES-1120 — "Svara som medicinskt besked": the published notice that
  // answered the case (the latest version's id), and who signed it when.
  answerNoticeId: z.string().nullable(),
  answeredAt: z.string().nullable(),
  answeredByDoctorId: z.string().nullable(),
});
export type MedicalReviewCaseSummary = z.infer<
  typeof medicalReviewCaseSummarySchema
>;

export const medicalReviewCaseDetailSchema =
  medicalReviewCaseSummarySchema.extend({
    // The patient's own answers (F-04), as last saved or submitted.
    originalAnswers: z.record(z.string(), z.unknown()).nullable(),
    // The patient's own words (chat message or shared reflection).
    originalText: z.string().nullable(),
    // The coach's note when she sent it on; never the patient's words.
    coachNote: z.string().nullable(),
  });
export type MedicalReviewCaseDetail = z.infer<
  typeof medicalReviewCaseDetailSchema
>;

// TES-1040 — an admin in support mode reads a case's status and flags, never
// the patient's own words from the chat or a shared reflection. Simon
// 2026-10-01: the quoted text is replaced with exactly this string.
export const MEDICAL_REVIEW_CASE_ADMIN_MASK =
  "(chattinnehåll visas inte i läsläge)";
